import { ethers } from 'ethers';
import { hexZeroPad, id } from 'ethers/lib/utils';
import { isNativeCoin, getRpcUrl } from './metamaskHelper';

export default class TransactionWatcher {
    constructor(_coin, _recepientAddress, _orderKey, _chainId, _tokenAddress = null) {
        this.recepientAddress = _recepientAddress;
        this.orderKey = _orderKey;
        this.coin = _coin;
        this.chainId = _chainId;
        if (_tokenAddress) {
            this.tokenAddress = _tokenAddress;
        }
        const rpcURL = getRpcUrl(parseInt(this.chainId, 16));
        this.provider = new ethers.providers.JsonRpcProvider(rpcURL);
    }

    watchTransaction(callback) {
        if (isNativeCoin(this.coin, parseInt(this.chainId, 16))) {
            this.watchNativeCoinTransaction(callback);
            this.getPreviousNativeCoinTx(callback);
            return;
        }
        this.watchErc20Logs(callback);
        this.getPreviousErc20Tx(callback);
    }

    unwatchTransaction() {
        this.provider.removeAllListeners();
    }

    watchNativeCoinTransaction(callback) {
        try {
            // For native coin transfer, there is no event log, so we have to
            // track the block and check the transaction data.
            this.provider.on('block', async (blockNumber) => {
                const transactions = await this.provider.getBlockWithTransactions(blockNumber);
                const transaction = transactions.transactions.find(
                    (tx) => tx?.to === this.recepientAddress,
                );
                if (transaction?.data?.includes(id(this.orderKey).substring(2))) {
                    callback(transaction.from, transaction.hash);
                }
            });
        } catch (e) {
            console.error(e);
        }
    }

    async getPreviousNativeCoinTx(callback) {
        try {
            // Try to track last 5 blocks transaction, should be sufficient,
            // considering blocktime is around 10 - 12 seconds these days.
            const blockNumber = await this.provider.getBlockNumber();
            const getTxPromises = await Promise.all([
                this.provider.getBlockWithTransactions(blockNumber),
                this.provider.getBlockWithTransactions(blockNumber - 1),
                this.provider.getBlockWithTransactions(blockNumber - 2),
                this.provider.getBlockWithTransactions(blockNumber - 3),
                this.provider.getBlockWithTransactions(blockNumber - 4),
            ]);
            getTxPromises.forEach((tx) => {
                if (tx?.data?.includes(id(this.orderKey).substring(2))) {
                    callback(tx.from, tx.hash);
                }
            });
        } catch (e) {
            console.error(e);
        }
    }

    watchErc20Logs(callback) {
        try {
            // For ERC20 transfer, we can track the event log.
            // This transaction filter will only track the ERC-20 transfer
            // to the recepient address on the token contract.
            const filter = {
                address: this.tokenAddress,
                topic: [
                    ethers.utils.id('Transfer(address,address,uint256)'),
                    null,
                    [
                        hexZeroPad(this.recepientAddress, 32),
                    ],
                ],
            };
            this.provider.on(filter, async (log) => {
                const tx = await this.provider.getTransaction(log.transactionHash);
                if (tx?.data?.includes(id(this.orderKey).substring(2))) {
                    callback(tx.from, tx.hash);
                }
            });
        } catch (e) {
            console.error(e);
        }
    }

    async getPreviousErc20Tx(callback) {
        try {
            const filter = {
                address: this.tokenAddress,
                fromBlock: -10,
                topic: [
                    ethers.utils.id('Transfer(address,address,uint256)'),
                    null,
                    [
                        hexZeroPad(this.recepientAddress, 32),
                    ],
                ],
            };
            const logs = await this.provider.getLogs(filter);
            const getTxPromises = await Promise.all(
                logs.map((log) => this.provider.getTransaction(log.transactionHash)),
            );
            getTxPromises.forEach((tx) => {
                if (tx?.data?.includes(id(this.orderKey).substring(2))) {
                    callback(tx.from, tx.hash);
                }
            });
        } catch (e) {
             console.error(e);
        }
    }
}
